import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Grid,
  TextField,
  FormControl,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Alert,
  Card,
  CardContent,
  CardActions,
  Chip,
  CircularProgress,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Snackbar,
  useMediaQuery,
  useTheme,
  Tabs,
  Tab,
  RadioGroup,
  FormControlLabel as MuiFormControlLabel,
  Radio,
} from '@mui/material';
import { CheckCircle, Cancel } from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { createRaceCard, getUserRaceCards, deleteRaceCard, cleanupAllListeners, getApplicationsForRaceCard, updateApplicationStatus, clearUserCardsCache } from '../services/raceCardService';
import { getUserProfile } from '../services/userProfileService';
import { teamColors } from '../config/teamColors';
import {
  GAME_VERSIONS,
  DEFAULT_GAME_VERSION,
  DEFAULT_GAME_VERSION_CAREER,
  getGameVersionOptions,
  getTracksForGame,
} from '../config/f1GameTracks';
import styles from '../styles/Home.module.css';

// Helper function to convert 24-hour time to 12-hour format with AM/PM and timezone
const formatTimeWithAMPM = (timeString, region) => {
  if (!timeString) return 'Unknown';
  
  // Check if it's already in 12-hour format with AM/PM
  if (timeString.includes('AM') || timeString.includes('PM')) {
    // If it already has AM/PM but no timezone, add the timezone
    if (region && !timeString.includes('(')) {
      const timezoneAbbr = getRegionAbbreviation(region);
      return `${timeString} (${timezoneAbbr})`;
    }
    return timeString;
  }
  
  // Parse 24-hour format (HH:MM)
  const [hours, minutes] = timeString.split(':').map(Number);
  
  if (isNaN(hours) || isNaN(minutes)) {
    return timeString; // Return original if parsing fails
  }
  
  const hour12 = ((hours + 11) % 12) + 1;
  const ampm = hours < 12 ? 'AM' : 'PM';
  const minutesStr = minutes.toString().padStart(2, '0');
  
  const formattedTime = `${hour12}:${minutesStr} ${ampm}`;
  
  // Add timezone abbreviation if region is provided
  if (region) {
    const timezoneAbbr = getRegionAbbreviation(region);
    return `${formattedTime} (${timezoneAbbr})`;
  }
  
  return formattedTime;
};

// Options for 2 Player Career tab (Tab 0)
const careerTeamOptions = [
  'Any team',
  'Red Bull',
  'Racing Bulls',
  'Ferrari',
  'McLaren',
  'Mercedes',
  'Aston Martin',
  'Haas',
  'Alpine',
  'Kick Sauber',
  'Williams'
];

const careerAiLevelOptions = [
  '0-40',
  '40-50',
  '60-70',
  '70-80',
  '80-90',
  '90-100',
  '100+'
];

// League Startup: same as career plus Multiple Tiers (at top)
const leagueStartupAiLevelOptions = ['Multiple Tiers', ...careerAiLevelOptions];

const careerRivalryTypeOptions = [
  'Any rivalry',
  'Teammates',
  'Rival Teams'
];

const careerRaceDistanceOptions = [
  '5 laps',  
  '25%',
  '35%',
  '50%',
  '100%'
];

// Options for Unranked Lobby tab (Tab 1)
const unrankedRaceDistanceOptions = [
  '5 laps',
  '25%',
  '35%',
  '50%',
  '100%'
];

const unrankedQualifyingOptions = [
  'No Quali',
  'One Shot',
  'Short Quali',
  'Full Quali'
];

const unrankedDamageOptions = [
  'Damage On',
  'Damage Off'
];

const leagueStartupAssistsOptions = [
  'No assists',
  'Some Assists',
  'All Assists'
];

// Custom schedule options for Unranked Lobby
const unrankedRunTimeOptions = [
  '30 mins',
  '1-2 hours',
  '2+ hours'
];

// Options for Reserve a Race tab (Tab 2)
const reserveAiLevelOptions = [
  '0-40',
  '40-50',
  '60-70',
  '70-80',
  '80-90',
  '90-100',
  '100+'
];

const reserveRivalryTypeOptions = [
  'Any rivalry',
  'Teammates',
  'Rival Teams'
];

const reserveRaceDistanceOptions = [
  '5 laps',  
  '25%',
  '50%',
  '100%'
];

const reserveCarClassOptions = [
  'F1',
  'F2',
  'F1 World'
];

const reserveQualifyingFormatOptions = [
  'No Quali',
  'One shot',
  'Short Quali',
  'Full Quali'
];

const reservePerformanceOptions = [
  'Equal',
  'Realistic'
];

// Shared options (same for all tabs)
const regionOptions = [
  'Europe',
  'North America',
  'Asia',
  'Australia',
  'South America'
];

const timeOfDayOptions = [
  'Any time',
  'Morning',
  'Afternoon',
  'Evenings',
];

const frequencyOptions = [
  'Once a week',
  'Twice a week',
  'Weekends',
  'Weekdays',
];

const crossplayOptions = [
  'Enabled',
  'PS only',
  'Xbox only',
  'PC only',
];

const daysOfWeek = [
  'Monday',
  'Tuesday',
  'Wednesday',
  'Thursday',
  'Friday',
  'Saturday',
  'Sunday',
  'Any days'
];

// Helper to map region to abbreviation
const getRegionAbbreviation = (region) => {
  switch (region) {
    case 'North America':
      return 'ET'; // Eastern Time
    case 'Europe':
      return 'GMT'; // Greenwich Mean Time
    case 'Asia':
      return 'SGT'; // Singapore Time
    case 'Australia':
      return 'AET'; // Australian Eastern Time
    case 'South America':
      return 'BRT'; // Brasilia Time
    default:
      return 'GMT';
  }
};

// Helper to generate 15-minute interval times in AM/PM with dynamic abbreviation
const getTimeOptions15Min = (region) => {
  const abbr = getRegionAbbreviation(region);
  const options = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      const hour12 = ((h + 11) % 12) + 1;
      const ampm = h < 12 ? 'AM' : 'PM';
      const label = `${hour12}:${m.toString().padStart(2, '0')} ${ampm} (${abbr})`;
      const value = `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      options.push({ label, value });
    }
  }
  return options;
};

// Dynamic validation schema based on tab index
const getValidationSchema = (tabIndex) => {
  const baseSchema = {
    gameVersion: Yup.string().required('Game version is required').test('not-default', 'Game version is required', value => value !== 'Select option'),
    timezone: Yup.string().required('Region is required').test('not-default', 'Region is required', value => value !== 'Select option'),
    crossplay: Yup.string().required('Crossplay preference is required').test('not-default', 'Crossplay preference is required', value => value !== 'Select option'),
    lobbyStreamed: Yup.boolean(),
    streamingPlatform: Yup.string(),
  };

  switch (tabIndex) {
    case 0: // 2 Player Career
      return Yup.object({
        ...baseSchema,
        teamPreference: Yup.string().required('Team preference is required').test('not-default', 'Team preference is required', value => value !== 'Select option'),
        aiLevel: Yup.string().required('AI level is required').test('not-default', 'AI level is required', value => value !== 'Select option'),
        rivalryType: Yup.string().required('Rivalry type is required').test('not-default', 'Rivalry type is required', value => value !== 'Select option'),
        timeOfDay: Yup.string().required('Time of day is required').test('not-default', 'Time of day is required', value => value !== 'Select option'),
        frequency: Yup.string().required('Frequency is required').test('not-default', 'Frequency is required', value => value !== 'Select option'),
        raceDistance: Yup.string().required('Race distance is required').test('not-default', 'Race distance is required', value => value !== 'Select option'),
        preferredDays: Yup.object().test(
          'at-least-one-day',
          'Please select at least one preferred day',
          (value) => Object.values(value).some((day) => day === true)
        ),
      });

    case 1: // Unranked Lobby
      return Yup.object({
        ...baseSchema,
        aiLevel: Yup.string().required('Qualifying preference is required').test('not-default', 'Qualifying preference is required', value => value !== 'Select option'),
        rivalryType: Yup.string().required('Damage is required').test('not-default', 'Damage is required', value => value !== 'Select option'),
        raceDistance: Yup.string().required('Race distance is required').test('not-default', 'Race distance is required', value => value !== 'Select option'),
        timeOfDay: Yup.string().required('Start time is required'),
        frequency: Yup.string().required('Run time is required').test('not-default', 'Run time is required', value => value !== 'Select option'),
        raceDate: Yup.date().required('Lobby date is required'),
        lobbyStreamed: Yup.boolean(),
        streamingPlatform: Yup.string().test(
          'required-if-streamed',
          'Streaming platform is required',
          function (value) {
            const { lobbyStreamed } = this.parent;
            if (lobbyStreamed) {
              return !!value;
            }
            return true;
          }
        ),
        streamLink: Yup.string().test(
          'required-if-streaming',
          'Stream link is required for streaming platforms',
          function (value) {
            const { lobbyStreamed, streamingPlatform } = this.parent;
            if (lobbyStreamed && (streamingPlatform === 'YouTube' || streamingPlatform === 'Twitch')) {
              if (streamingPlatform === 'YouTube') {
                return !!value && value.toLowerCase().includes('youtube');
              } else if (streamingPlatform === 'Twitch') {
                return !!value && value.toLowerCase().includes('twitch');
              }
            }
            return true;
          }
        ),
      });

    case 2: // Reserve a Race
      return Yup.object({
        ...baseSchema,
        track: Yup.string().required('Track is required').test('not-default', 'Track is required', value => value !== 'Select option'),
        aiLevel: Yup.string().required('Estimated AI level is required').test('not-default', 'Estimated AI level is required', value => value !== 'Select option'),
        raceDistance: Yup.string().required('Race distance is required').test('not-default', 'Race distance is required', value => value !== 'Select option'),
        carClass: Yup.string().required('Car class is required').test('not-default', 'Car class is required', value => value !== 'Select option'),
        qualifyingFormat: Yup.string().required('Qualifying format is required').test('not-default', 'Qualifying format is required', value => value !== 'Select option'),
        performance: Yup.string().required('Performance is required').test('not-default', 'Performance is required', value => value !== 'Select option'),
        timeOfDay: Yup.string().required('Start time is required'),
        raceDate: Yup.date().required('Race date is required'),
        seats: Yup.number().min(1).max(10).required('Seats is required'),
        lobbyStreamed: Yup.boolean(),
        streamingPlatform: Yup.string().test(
          'required-if-streamed',
          'Streaming platform is required',
          function (value) {
            const { lobbyStreamed } = this.parent;
            if (lobbyStreamed) {
              return !!value;
            }
            return true;
          }
        ),
        streamLink: Yup.string().test(
          'required-if-streaming',
          'Stream link is required for streaming platforms',
          function (value) {
            const { lobbyStreamed, streamingPlatform } = this.parent;
            if (lobbyStreamed && (streamingPlatform === 'YouTube' || streamingPlatform === 'Twitch')) {
              if (streamingPlatform === 'YouTube') {
                return !!value && value.toLowerCase().includes('youtube');
              } else if (streamingPlatform === 'Twitch') {
                return !!value && value.toLowerCase().includes('twitch');
              }
            }
            return true;
          }
        ),
        discordLink: Yup.string().test(
          'discord-url',
          'Please enter a valid Discord invite or server link (e.g. discord.gg/... or discord.com/...)',
          (value) => {
            if (!value || value.trim() === '') return true;
            const v = value.toLowerCase();
            return v.includes('discord') || v.includes('dsc.gg');
          }
        ),
      });
    case 3: // Commentary (customized)
      return Yup.object({
        ...baseSchema,
        track: Yup.string().required('Track is required').test('not-default', 'Track is required', value => value !== 'Select option'),
        raceDistance: Yup.string().required('Race distance is required').test('not-default', 'Race distance is required', value => value !== 'Select option'),
        carClass: Yup.string().required('Car class is required').test('not-default', 'Car class is required', value => value !== 'Select option'),
        qualifyingFormat: Yup.string().required('Qualifying format is required').test('not-default', 'Qualifying format is required', value => value !== 'Select option'),
        timeOfDay: Yup.string().required('Start time is required'),
        raceDate: Yup.date().required('Race date is required'),
        lobbyStreamed: Yup.boolean(),
        streamingPlatform: Yup.string().test(
          'required-if-streamed',
          'Streaming platform is required',
          function (value) {
            const { lobbyStreamed } = this.parent;
            if (lobbyStreamed) {
              return !!value;
            }
            return true;
          }
        ),
        streamLink: Yup.string().test(
          'required-if-streaming',
          'Stream link is required for streaming platforms',
          function (value) {
            const { lobbyStreamed, streamingPlatform } = this.parent;
            if (lobbyStreamed && (streamingPlatform === 'YouTube' || streamingPlatform === 'Twitch')) {
              if (streamingPlatform === 'YouTube') {
                return !!value && value.toLowerCase().includes('youtube');
              } else if (streamingPlatform === 'Twitch') {
                return !!value && value.toLowerCase().includes('twitch');
              }
            }
            return true;
          }
        ),
        discordLink: Yup.string().test(
          'discord-url',
          'Please enter a valid Discord invite or server link (e.g. discord.gg/... or discord.com/...)',
          (value) => {
            if (!value || value.trim() === '') return true;
            const v = value.toLowerCase();
            return v.includes('discord') || v.includes('dsc.gg');
          }
        ),
      });

    case 4: // League Startup (no team preference or rivalry type)
      return Yup.object({
        ...baseSchema,
        aiLevel: Yup.string().required('AI level is required').test('not-default', 'AI level is required', value => value !== 'Select option'),
        damage: Yup.string().required('Damage is required').test('not-default', 'Damage is required', value => value !== 'Select option'),
        assists: Yup.string().required('Assists is required').test('not-default', 'Assists is required', value => value !== 'Select option'),
        qualifyingFormat: Yup.string().required('Qualifying format is required').test('not-default', 'Qualifying format is required', value => value !== 'Select option'),
        timeOfDay: Yup.string().required('Start time is required'),
        leagueStartDate: Yup.date().required('Start Date is required').nullable(),
        races: Yup.number().min(1, 'Must be between 1 and 22').max(22, 'Must be between 1 and 22').required('Races is required'),
        raceDistance: Yup.string().required('Race distance is required').test('not-default', 'Race distance is required', value => value !== 'Select option'),
        preferredDays: Yup.object().test(
          'at-least-one-day',
          'Please select at least one preferred day',
          (value) => Object.values(value).some((day) => day === true)
        ),
        lobbyStreamed: Yup.boolean(),
        streamingPlatform: Yup.string().test(
          'required-if-streamed',
          'Streaming platform is required',
          function (value) {
            const { lobbyStreamed } = this.parent;
            if (lobbyStreamed) {
              return !!value;
            }
            return true;
          }
        ),
        streamLink: Yup.string().test(
          'required-if-streaming',
          'Stream link is required for streaming platforms',
          function (value) {
            const { lobbyStreamed, streamingPlatform } = this.parent;
            if (lobbyStreamed && (streamingPlatform === 'YouTube' || streamingPlatform === 'Twitch')) {
              if (streamingPlatform === 'YouTube') {
                return !!value && value.toLowerCase().includes('youtube');
              } else if (streamingPlatform === 'Twitch') {
                return !!value && value.toLowerCase().includes('twitch');
              }
            }
            return true;
          }
        ),
        discordLink: Yup.string().test(
          'discord-url',
          'Please enter a valid Discord invite or server link (e.g. discord.gg/... or discord.com/...)',
          (value) => {
            if (!value || value.trim() === '') return true;
            const v = value.toLowerCase();
            return v.includes('discord') || v.includes('dsc.gg');
          }
        ),
      });

    default:
      return Yup.object({
        ...baseSchema,
        teamPreference: Yup.string().required('Team preference is required').test('not-default', 'Team preference is required', value => value !== 'Select option'),
        aiLevel: Yup.string().required('AI level is required').test('not-default', 'AI level is required', value => value !== 'Select option'),
        rivalryType: Yup.string().required('Rivalry type is required').test('not-default', 'Rivalry type is required', value => value !== 'Select option'),
        timeOfDay: Yup.string().required('Time of day is required').test('not-default', 'Time of day is required', value => value !== 'Select option'),
        frequency: Yup.string().required('Frequency is required').test('not-default', 'Frequency is required', value => value !== 'Select option'),
        raceDistance: Yup.string().required('Race distance is required').test('not-default', 'Race distance is required', value => value !== 'Select option'),
        preferredDays: Yup.object().test(
          'at-least-one-day',
          'Please select at least one preferred day',
          (value) => Object.values(value).some((day) => day === true)
        ),
      });
  }
};

// Field labels for each tab
const getFieldLabels = (tabIndex) => {
  const baseLabels = { gameVersion: 'Game' };
  switch (tabIndex) {
    case 0: // 2 Player Career
      return {
        ...baseLabels,
        teamPreference: 'Team Preference',
        aiLevel: 'AI Level',
        rivalryType: 'Rivalry Type',
        raceDistance: 'Race Distance',
        timezone: 'Region',
        timeOfDay: 'Time of Day',
        frequency: 'Frequency',
        crossplay: 'Crossplay',
        preferredDays: 'Preferred Days'
      };
    case 1: // Unranked Lobby
      return {
        ...baseLabels,
        teamPreference: 'Car Type',
        aiLevel: 'Qualifying Preference',
        rivalryType: 'Damage',
        raceDistance: 'Race Distance',
        timezone: 'Region',
        timeOfDay: 'Start Time (Est)',
        frequency: 'Run Time',
        crossplay: 'Crossplay',
        preferredDays: 'Preferred Days'
      };
    case 2: // Reserve a Race
      return {
        ...baseLabels,
        teamPreference: 'Team Preference',
        track: 'Track',
        aiLevel: 'AI Level (Est)',
        rivalryType: 'Rivalry Type',
        raceDistance: 'Race Distance',
        carClass: 'Car Class',
        qualifyingFormat: 'Qualifying Format',
        performance: 'Performance',
        fullTimeSeat: 'Full Time Seat Available',
        timezone: 'Region',
        timeOfDay: 'Start Time',
        frequency: 'Race Date',
        crossplay: 'Crossplay',
        preferredDays: 'Preferred Days'
      };
    case 3: // Commentary (same labels as Reserve for now)
      return {
        ...baseLabels,
        teamPreference: 'Team Preference',
        track: 'Track',
        aiLevel: 'AI Level (Est)',
        rivalryType: 'Rivalry Type',
        raceDistance: 'Race Distance',
        carClass: 'Car Class',
        qualifyingFormat: 'Qualifying Format',
        fullTimeSeat: 'Paid Role',
        timezone: 'Region',
        timeOfDay: 'Start Time',
        frequency: 'Race Date',
        crossplay: 'Crossplay',
        preferredDays: 'Preferred Days'
      };
    case 4: // League Startup
      return {
        ...baseLabels,
        aiLevel: 'AI Level',
        damage: 'Damage',
        assists: 'Assists',
        qualifyingFormat: 'Quali Length',
        raceDistance: 'Race Distance',
        timezone: 'Region',
        timeOfDay: 'Start Time',
        leagueStartDate: 'Start Date',
        frequency: 'Races',
        races: 'Races',
        crossplay: 'Crossplay',
        preferredDays: 'Race Days'
      };
    default:
      return {
        ...baseLabels,
        teamPreference: 'Team Preference',
        aiLevel: 'AI Level',
        rivalryType: 'Rivalry Type',
        raceDistance: 'Race Distance',
        timezone: 'Region',
        timeOfDay: 'Time of Day',
        frequency: 'Frequency',
        crossplay: 'Crossplay',
        preferredDays: 'Preferred Days'
      };
  }
};

// Add this helper function if not already present
function formatDateDDMMYYYY(date) {
  if (!date) return 'Unknown';
  const d = new Date(date);
  const day = d.getDate().toString().padStart(2, '0');
  const month = (d.getMonth() + 1).toString().padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

function CreateRaceCard() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, isEmailVerified, user } = useAuth();
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [userRaceCards, setUserRaceCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applications, setApplications] = useState({});
  const [processingApplication, setProcessingApplication] = useState(null);
  const [activeTab, setActiveTab] = useState(location.state?.activeTab || 0);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Scroll to top when landing on create card page (e.g. from homepage)
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // Move these variables to component scope
  let unsubscribe = null;
  let applicationsUnsubscribe = null;

  const setupUserRaceCards = useCallback(() => {
    if (isAuthenticated() && user) {
      // eslint-disable-next-line react-hooks/exhaustive-deps
      unsubscribe = getUserRaceCards(user.uid, (cards) => {
        console.log('User race cards updated:', cards);
        setUserRaceCards(cards);
        setLoading(false);
        
        // Clear existing application listeners
        if (applicationsUnsubscribe) {
          applicationsUnsubscribe();
        }
        
        // Set up applications listeners for each card
        if (cards && cards.length > 0) {
          console.log('Setting up applications listeners for cards:', cards.map(c => c.id));
          
          // Set up listeners for each card
          const unsubscribers = cards.map(card => {
            return getApplicationsForRaceCard(card.id, (apps) => {
              console.log(`Applications updated for card ${card.id}:`, apps);
              setApplications(prev => ({
                ...prev,
                [card.id]: apps
              }));
            });
          });
          
          // Store all unsubscribers
          // eslint-disable-next-line react-hooks/exhaustive-deps
          // eslint-disable-next-line react-hooks/exhaustive-deps
          applicationsUnsubscribe = () => {
            unsubscribers.forEach(unsub => unsub());
          };
        } else {
          console.log('No race cards found, clearing applications');
          setApplications({});
        }
      });
    } else {
      console.log('User not authenticated, clearing race cards and applications');
      setUserRaceCards([]);
      setApplications({});
      setLoading(false);
    }
  }, [isAuthenticated, user]);

  useEffect(() => {
    setupUserRaceCards();

    // Cleanup function
    return () => {
      console.log('Cleaning up listeners');
      if (unsubscribe) {
        unsubscribe();
      }
      if (applicationsUnsubscribe) {
        applicationsUnsubscribe();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user]);

  // Debug applications state changes
  useEffect(() => {
    console.log('Applications state changed:', applications);
  }, [applications]);

  // Handle scroll to active cards section
  useEffect(() => {
    console.log('Scroll effect triggered:', { 
      scrollToActiveCards: location.state?.scrollToActiveCards, 
      userRaceCardsLength: userRaceCards.length,
      loading 
    });
    
    if (location.state?.scrollToActiveCards && !loading) {
      console.log('Attempting to scroll to active cards section');
      // Wait a bit for the component to render, then scroll to the active cards section
      setTimeout(() => {
        const activeCardsSection = document.querySelector('[data-section="active-cards"]');
        console.log('Found active cards section:', activeCardsSection);
        if (activeCardsSection) {
          activeCardsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          console.log('Scrolled to active cards section');
          // Clear the scroll state after scrolling
          navigate(location.pathname, { replace: true });
        } else {
          console.log('Active cards section not found');
        }
      }, 1000); // Increased timeout to ensure component is fully rendered
    }
  }, [location.state?.scrollToActiveCards, userRaceCards, loading, navigate, location.pathname]);

  const handleDeleteCard = async (cardId) => {
    try {
      await deleteRaceCard(cardId);
      
      // Clear the cache to force a refresh
      if (user) {
        clearUserCardsCache(user.uid);
      }
      
      // Force a refresh by clearing and re-setting up the listener
      if (unsubscribe) {
        unsubscribe();
      }
      setupUserRaceCards();
      
      setSuccess('Race card deleted successfully!');
    } catch (error) {
      console.error('Error deleting race card:', error);
      setError('Failed to delete race card');
    }
  };

  const handleApplicationResponse = async (applicationId, status) => {
    console.log('Handling application response:', { applicationId, status });
    setProcessingApplication(applicationId);
    try {
      await updateApplicationStatus(applicationId, status);
      console.log('Application status updated successfully');
      
      // Find the card for this application
      let cardForApp = null;
      for (const card of userRaceCards) {
        const app = applications[card.id]?.find(app => app.id === applicationId);
        if (app) {
          cardForApp = card;
          break;
        }
      }
      if (status === 'accepted') {
        if (cardForApp && (cardForApp.cardType === 'career' || cardForApp.cardType === 'commentary')) {
          // 2 Player Career / Commentary: auto-reject all others
          const pendingApplications = applications[cardForApp.id]?.filter(app => 
            app.status === 'pending' && app.id !== applicationId
          ) || [];
          for (const app of pendingApplications) {
            await updateApplicationStatus(app.id, 'rejected');
          }
        } else if (cardForApp && (cardForApp.cardType === 'unranked' || cardForApp.cardType === 'reserve')) {
          // Unranked/Reserve/Commentary: auto-reject all others if full
          const acceptedCount = applications[cardForApp.id]?.filter(app => app.status === 'accepted' || (app.id === applicationId && status === 'accepted')).length || 0;
          let maxAccepted = 1;
          if (cardForApp.cardType === 'reserve') {
            maxAccepted = cardForApp.seats || 1;
          } else if (cardForApp.cardType === 'unranked') {
            maxAccepted = 19;
          }
          if (acceptedCount >= maxAccepted) {
            const pendingApplications = applications[cardForApp.id]?.filter(app => 
              app.status === 'pending' && app.id !== applicationId
            ) || [];
            for (const app of pendingApplications) {
              await updateApplicationStatus(app.id, 'rejected');
            }
          }
        }
      }
      setSuccess(`Application ${status === 'accepted' ? 'accepted' : 'rejected'} successfully!`);
    } catch (error) {
      console.error('Error updating application:', error);
      setError(`Failed to ${status === 'accepted' ? 'accept' : 'reject'} application`);
    } finally {
      setProcessingApplication(null);
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  // Function to check which card types are available
  const getAvailableCardTypes = () => {
    const existingTypes = userRaceCards.map(card => card.cardType);
    return {
      career: !existingTypes.includes('career'),
      unranked: !existingTypes.includes('unranked'),
      reserve: !existingTypes.includes('reserve'),
      commentary: !existingTypes.includes('commentary'),
      leagueStartup: !existingTypes.includes('leagueStartup')
    };
  };

  const availableTypes = getAvailableCardTypes();

  // Reusable Race Card Form Component
  const RaceCardForm = ({ tabIndex }) => {
    const formik = useFormik({
      initialValues: {
        gameVersion: tabIndex === 0 ? DEFAULT_GAME_VERSION_CAREER : DEFAULT_GAME_VERSION,
        teamPreference: 'Select option',
        aiLevel: 'Select option',
        rivalryType: 'Select option',
        damage: 'Select option',
        assists: 'Select option',
        timezone: 'Select option',
        timeOfDay: tabIndex === 1 || tabIndex === 2 || tabIndex === 3 || tabIndex === 4 ? '12:00' : 'Select option',
        preferredDays: daysOfWeek.reduce((acc, day) => {
          acc[day] = false;
          return acc;
        }, {}),
        frequency: 'Select option',
        crossplay: 'Enabled',
        raceDistance: 'Select option',
        races: 1,
        leagueStartDate: null,
        track: 'Select option',
        raceDate: tabIndex === 2 || tabIndex === 1 || tabIndex === 3 ? new Date() : null,
        carClass: 'Select option',
        qualifyingFormat: 'Select option',
        performance: 'Select option',
        fullTimeSeat: false,
        lobbyStreamed: false,
        streamingPlatform: '',
        streamLink: '',
        discordLink: '',
        seats: 1,
      },
      validationSchema: getValidationSchema(tabIndex),
      onSubmit: async (values) => {
        try {
          if (!isAuthenticated()) {
            setError('Please log in to create a race card');
            return;
          }

          if (!isEmailVerified()) {
            setError('Please verify your email before creating a race card');
            return;
          }

          // Get user's EA ID from their profile
          const userProfile = await getUserProfile(user.uid);
          if (!userProfile || !userProfile.eaId) {
            setError('Please complete your profile with your EA ID before creating a race card');
            return;
          }

          let raceCardData = {};

          // Handle different tabs with different data structures
          switch (tabIndex) {
            case 0: // 2 Player Career
              raceCardData = {
                title: `2 Player Career`,
                gameVersion: values.gameVersion,
                teamPreference: values.teamPreference,
                aiLevel: values.aiLevel,
                rivalryType: values.rivalryType,
                timezone: values.timezone,
                timeOfDay: values.timeOfDay,
                frequency: values.frequency,
                races: values.races,
                crossplay: values.crossplay,
                raceDistance: values.raceDistance,
                preferredDays: values.preferredDays,
                createdByEmail: user.email,
                createdByEaId: userProfile.eaId,
                cardType: 'career'
              };
              break;

            case 1: // Unranked Lobby
              raceCardData = {
                title: `Unranked Lobby`,
                gameVersion: values.gameVersion,
                carType: 'F1',
                qualifyingPreference: values.aiLevel, // Using aiLevel field for qualifyingPreference
                damage: values.rivalryType, // Using rivalryType field for damage
                raceDistance: values.raceDistance,
                timezone: values.timezone,
                estimatedStartTime: values.timeOfDay, // Using timeOfDay field for estimatedStartTime
                runTime: values.frequency, // Using frequency field for runTime
                crossplay: values.crossplay,
                lobbyStreamed: values.lobbyStreamed,
                streamingPlatform: values.lobbyStreamed ? values.streamingPlatform : '',
                streamLink: values.lobbyStreamed && (values.streamingPlatform === 'YouTube' || values.streamingPlatform === 'Twitch') ? values.streamLink : '',
                raceDate: values.raceDate, // NEW: Save the date
                createdByEmail: user.email,
                createdByEaId: userProfile.eaId,
                cardType: 'unranked'
              };
              break;

            case 2: // Reserve a Race
              raceCardData = {
                title: `Reserve a Race`,
                gameVersion: values.gameVersion,
                track: values.track,
                estAiLevel: values.aiLevel, // Using aiLevel field for estAiLevel
                raceDistance: values.raceDistance,
                carClass: values.carClass,
                qualifyingFormat: values.qualifyingFormat,
                performance: values.performance,
                timezone: values.timezone,
                startTime: values.timeOfDay, // Using timeOfDay field for startTime
                raceDate: values.raceDate,
                crossplay: values.crossplay,
                fullTimeSeat: values.fullTimeSeat,
                seats: values.seats,
                lobbyStreamed: values.lobbyStreamed,
                streamingPlatform: values.lobbyStreamed ? values.streamingPlatform : '',
                streamLink: values.lobbyStreamed && (values.streamingPlatform === 'YouTube' || values.streamingPlatform === 'Twitch') ? values.streamLink : '',
                discordLink: values.discordLink || '',
                createdByEmail: user.email,
                createdByEaId: userProfile.eaId,
                cardType: 'reserve'
              };
              break;

            case 3: // Commentary
              raceCardData = {
                title: `Commentary Role`,
                gameVersion: values.gameVersion,
                track: values.track,
                raceDistance: values.raceDistance,
                carClass: values.carClass,
                qualifyingFormat: values.qualifyingFormat,
                performance: values.performance,
                timezone: values.timezone,
                startTime: values.timeOfDay,
                raceDate: values.raceDate,
                crossplay: values.crossplay,
                fullTimeSeat: values.fullTimeSeat, // used as Paid Role for commentary
                lobbyStreamed: values.lobbyStreamed,
                streamingPlatform: values.lobbyStreamed ? values.streamingPlatform : '',
                streamLink: values.lobbyStreamed && (values.streamingPlatform === 'YouTube' || values.streamingPlatform === 'Twitch') ? values.streamLink : '',
                discordLink: values.discordLink || '',
                createdByEmail: user.email,
                createdByEaId: userProfile.eaId,
                cardType: 'commentary'
              };
              break;

            case 4: // League Startup (no team preference or rivalry type)
              raceCardData = {
                title: `League Startup`,
                gameVersion: values.gameVersion,
                aiLevel: values.aiLevel,
                damage: values.damage,
                assists: values.assists,
                qualifyingFormat: values.qualifyingFormat,
                timezone: values.timezone,
                timeOfDay: values.timeOfDay,
                races: values.races,
                leagueStartDate: values.leagueStartDate,
                crossplay: values.crossplay,
                raceDistance: values.raceDistance,
                preferredDays: values.preferredDays,
                lobbyStreamed: values.lobbyStreamed,
                streamingPlatform: values.lobbyStreamed ? values.streamingPlatform : '',
                streamLink: values.lobbyStreamed && (values.streamingPlatform === 'YouTube' || values.streamingPlatform === 'Twitch') ? values.streamLink : '',
                discordLink: values.discordLink || '',
                createdByEmail: user.email,
                createdByEaId: userProfile.eaId,
                cardType: 'leagueStartup'
              };
              break;

            default:
              throw new Error('Invalid tab index');
          }

          await createRaceCard(raceCardData, user.uid);
          setSuccess('Race card created successfully!');
          
          // Clear user cards cache to force refresh
          clearUserCardsCache(user.uid);
          
          // Cleanup all listeners
          cleanupAllListeners();
          
          // Re-establish the listeners to get the updated data immediately
          setupUserRaceCards();
          
          // Scroll to active cards section
          const activeCardsSection = document.querySelector('[data-section="active-cards"]');
          if (activeCardsSection) {
            activeCardsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        } catch (error) {
          console.error('Error creating race card:', error);
          setError(error.message || 'Failed to create race card');
        }
      },
    });

    // Reset form when tab changes
    useEffect(() => {
      formik.resetForm({
        values: {
          gameVersion: tabIndex === 0 ? DEFAULT_GAME_VERSION_CAREER : DEFAULT_GAME_VERSION,
          teamPreference: 'Select option',
          aiLevel: 'Select option',
          rivalryType: 'Select option',
          damage: 'Select option',
          assists: 'Select option',
          timezone: 'Select option',
          timeOfDay: tabIndex === 1 || tabIndex === 2 || tabIndex === 3 || tabIndex === 4 ? '12:00' : 'Select option',
          preferredDays: daysOfWeek.reduce((acc, day) => {
            acc[day] = false;
            return acc;
          }, {}),
          frequency: 'Select option',
          crossplay: 'Enabled',
          raceDistance: 'Select option',
          races: 1,
          leagueStartDate: tabIndex === 4 ? new Date() : null,
          track: 'Select option',
          raceDate: tabIndex === 2 || tabIndex === 1 || tabIndex === 3 ? new Date() : null,
          carClass: 'Select option',
          qualifyingFormat: 'Select option',
          performance: 'Select option',
          fullTimeSeat: false,
          lobbyStreamed: false,
          streamingPlatform: '',
          streamLink: '',
          discordLink: '',
          seats: 1,
        }
      });
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tabIndex]);

    return (
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={isMobile ? 1.5 : 2}>
          {/* Preferences Container */}
          <Grid item xs={12}>
            <Box className={isMobile ? styles.formSectionMobile : styles.formSection}>
              {/* Race Preferences Section */}
              <Box className={isMobile ? styles.sectionBoxMobile : styles.sectionBox}>
                <Typography variant="h6" gutterBottom className={isMobile ? styles.sectionTitleMobile : styles.sectionTitle}>
                  {(tabIndex === 2 || tabIndex === 3) ? 'Race Details' : 'Race Preferences'}
                </Typography>
                <Grid container spacing={isMobile ? 1.5 : 3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                      {getFieldLabels(tabIndex).gameVersion}
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        id="gameVersion"
                        name="gameVersion"
                        value={formik.values.gameVersion}
                        onChange={(e) => {
                          const newVersion = e.target.value;
                          formik.setFieldValue('gameVersion', newVersion);
                          if (tabIndex === 2 || tabIndex === 3) {
                            const allowedTracks = getTracksForGame(newVersion);
                            if (!allowedTracks.includes(formik.values.track)) {
                              formik.setFieldValue('track', 'Select option');
                            }
                          }
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.gameVersion && Boolean(formik.errors.gameVersion)}
                        className={styles.selectBackground}
                      >
                        {getGameVersionOptions({ excludeF126: tabIndex === 0 }).map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.gameVersion && formik.errors.gameVersion && (
                        <Typography color="error" variant="caption" className={styles.errorText}>
                          {formik.errors.gameVersion}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {(tabIndex === 2 || tabIndex === 3) && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                        {getFieldLabels(tabIndex).track}
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          id="track"
                          name="track"
                          value={formik.values.track}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.track && Boolean(formik.errors.track)}
                          className={styles.selectBackground}
                        >
                          <MenuItem value="Select option" disabled>Select option</MenuItem>
                          {getTracksForGame(formik.values.gameVersion).map((track) => (
                            <MenuItem key={track} value={track}>
                              {track}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.track && formik.errors.track && (
                          <Typography color="error" variant="caption" className={styles.errorText}>
                            {formik.errors.track}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  )}

                  {tabIndex === 0 && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                        {getFieldLabels(tabIndex).teamPreference}
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          id="teamPreference"
                          name="teamPreference"
                          value={formik.values.teamPreference}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.teamPreference && Boolean(formik.errors.teamPreference)}
                          className={styles.selectBackground}
                        >
                          <MenuItem value="Select option" disabled>Select option</MenuItem>
                          {careerTeamOptions.map((team) => (
                            <MenuItem key={team} value={team}>
                              {team}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.teamPreference && formik.errors.teamPreference && (
                          <Typography color="error" variant="caption" className={styles.errorText}>
                            {formik.errors.teamPreference}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  )}

                  {tabIndex !== 3 && (
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                      {getFieldLabels(tabIndex).aiLevel}
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        id="aiLevel"
                        name="aiLevel"
                        value={formik.values.aiLevel}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.aiLevel && Boolean(formik.errors.aiLevel)}
                        className={styles.selectBackground}
                      >
                        <MenuItem value="Select option" disabled>Select option</MenuItem>
                        {tabIndex === 0 ? careerAiLevelOptions.map((level) => (
                          <MenuItem key={level} value={level}>
                            {level}
                          </MenuItem>
                        )) : tabIndex === 4 ? leagueStartupAiLevelOptions.map((level) => (
                          <MenuItem key={level} value={level}>
                            {level}
                          </MenuItem>
                        )) : tabIndex === 1 ? unrankedQualifyingOptions.map((qualifying) => (
                          <MenuItem key={qualifying} value={qualifying}>
                            {qualifying}
                          </MenuItem>
                        )) : reserveAiLevelOptions.map((level) => (
                          <MenuItem key={level} value={level}>
                            {level}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.aiLevel && formik.errors.aiLevel && (
                        <Typography color="error" variant="caption" className={styles.errorText}>
                          {formik.errors.aiLevel}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                  )}

                  {(tabIndex !== 2 && tabIndex !== 3 && tabIndex !== 4) && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                        {getFieldLabels(tabIndex).rivalryType}
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          id="rivalryType"
                          name="rivalryType"
                          value={formik.values.rivalryType}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.rivalryType && Boolean(formik.errors.rivalryType)}
                          className={styles.selectBackground}
                        >
                          <MenuItem value="Select option" disabled>Select option</MenuItem>
                          {tabIndex === 0 ? careerRivalryTypeOptions.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          )) : tabIndex === 1 ? unrankedDamageOptions.map((damage) => (
                            <MenuItem key={damage} value={damage}>
                              {damage}
                            </MenuItem>
                          )) : reserveRivalryTypeOptions.map((type) => (
                            <MenuItem key={type} value={type}>
                              {type}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.rivalryType && formik.errors.rivalryType && (
                          <Typography color="error" variant="caption" className={styles.errorText}>
                            {formik.errors.rivalryType}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  )}

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                      {getFieldLabels(tabIndex).raceDistance}
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        id="raceDistance"
                        name="raceDistance"
                        value={formik.values.raceDistance}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.raceDistance && Boolean(formik.errors.raceDistance)}
                        className={styles.selectBackground}
                      >
                        <MenuItem value="Select option" disabled>Select option</MenuItem>
                        {tabIndex === 0 || tabIndex === 4 ? careerRaceDistanceOptions.map((distance) => (
                          <MenuItem key={distance} value={distance}>
                            {distance}
                          </MenuItem>
                        )) : tabIndex === 1 ? unrankedRaceDistanceOptions.map((distance) => (
                          <MenuItem key={distance} value={distance}>
                            {distance}
                          </MenuItem>
                        )) : reserveRaceDistanceOptions.map((distance) => (
                          <MenuItem key={distance} value={distance}>
                            {distance}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.raceDistance && formik.errors.raceDistance && (
                        <Typography color="error" variant="caption" className={styles.errorText}>
                          {formik.errors.raceDistance}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {(tabIndex === 2 || tabIndex === 3) && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                        {getFieldLabels(tabIndex).carClass}
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          id="carClass"
                          name="carClass"
                          value={formik.values.carClass}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.carClass && Boolean(formik.errors.carClass)}
                          className={styles.selectBackground}
                        >
                          <MenuItem value="Select option" disabled>Select option</MenuItem>
                          {reserveCarClassOptions.map((carClass) => (
                            <MenuItem key={carClass} value={carClass}>
                              {carClass}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.carClass && formik.errors.carClass && (
                          <Typography color="error" variant="caption" className={styles.errorText}>
                            {formik.errors.carClass}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  )}

                  {(tabIndex === 2 || tabIndex === 3 || tabIndex === 4) && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                        {getFieldLabels(tabIndex).qualifyingFormat}
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          id="qualifyingFormat"
                          name="qualifyingFormat"
                          value={formik.values.qualifyingFormat}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.qualifyingFormat && Boolean(formik.errors.qualifyingFormat)}
                          className={styles.selectBackground}
                        >
                          <MenuItem value="Select option" disabled>Select option</MenuItem>
                          {reserveQualifyingFormatOptions.map((format) => (
                            <MenuItem key={format} value={format}>
                              {format}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.qualifyingFormat && formik.errors.qualifyingFormat && (
                          <Typography color="error" variant="caption" className={styles.errorText}>
                            {formik.errors.qualifyingFormat}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  )}

                  {tabIndex === 4 && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                        {getFieldLabels(tabIndex).damage}
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          id="damage"
                          name="damage"
                          value={formik.values.damage}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.damage && Boolean(formik.errors.damage)}
                          className={styles.selectBackground}
                        >
                          <MenuItem value="Select option" disabled>Select option</MenuItem>
                          {unrankedDamageOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.damage && formik.errors.damage && (
                          <Typography color="error" variant="caption" className={styles.errorText}>
                            {formik.errors.damage}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  )}

                  {tabIndex === 4 && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                        {getFieldLabels(tabIndex).assists}
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          id="assists"
                          name="assists"
                          value={formik.values.assists}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.assists && Boolean(formik.errors.assists)}
                          className={styles.selectBackground}
                        >
                          <MenuItem value="Select option" disabled>Select option</MenuItem>
                          {leagueStartupAssistsOptions.map((opt) => (
                            <MenuItem key={opt} value={opt}>
                              {opt}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.assists && formik.errors.assists && (
                          <Typography color="error" variant="caption" className={styles.errorText}>
                            {formik.errors.assists}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  )}

                  {tabIndex === 2 && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                        {getFieldLabels(tabIndex).performance}
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          id="performance"
                          name="performance"
                          value={formik.values.performance}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.performance && Boolean(formik.errors.performance)}
                          className={styles.selectBackground}
                        >
                          <MenuItem value="Select option" disabled>Select option</MenuItem>
                          {reservePerformanceOptions.map((performance) => (
                            <MenuItem key={performance} value={performance}>
                              {performance}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.performance && formik.errors.performance && (
                          <Typography color="error" variant="caption" className={styles.errorText}>
                            {formik.errors.performance}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  )}

                  {tabIndex === 2 && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                      Open Seats
                      </Typography>
                      <FormControl fullWidth>
                        <Select
                          id="seats"
                          name="seats"
                          value={formik.values.seats}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.seats && Boolean(formik.errors.seats)}
                          className={styles.selectBackground}
                        >
                          {[...Array(10)].map((_, i) => (
                            <MenuItem key={i + 1} value={i + 1}>{i + 1} {i + 1 === 1 ? 'seat' : 'seats'}</MenuItem>
                          ))}
                        </Select>
                        {formik.touched.seats && formik.errors.seats && (
                          <Typography color="error" variant="caption" className={styles.errorText}>
                            {formik.errors.seats}
                          </Typography>
                        )}
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              </Box>

              {/* Schedule Preferences Section */}
              <Box>
                <Typography variant="h6" gutterBottom className={isMobile ? styles.sectionTitleMobile : styles.sectionTitle}>
                  Schedule Preferences
                </Typography>
                <Grid container spacing={isMobile ? 1.5 : 3}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                      {getFieldLabels(tabIndex).timezone}
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        id="timezone"
                        name="timezone"
                        value={formik.values.timezone}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.timezone && Boolean(formik.errors.timezone)}
                        className={styles.selectBackground}
                      >
                        <MenuItem value="Select option" disabled>Select option</MenuItem>
                        {regionOptions.map((region) => (
                          <MenuItem key={region} value={region}>
                            {region}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.timezone && formik.errors.timezone && (
                        <Typography color="error" variant="caption" className={styles.errorText}>
                          {formik.errors.timezone}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                      {getFieldLabels(tabIndex).timeOfDay}
                    </Typography>
                  {tabIndex === 1 || tabIndex === 2 || tabIndex === 3 || tabIndex === 4 ? (
                      <FormControl fullWidth>
                        <Select
                          id="timeOfDay"
                          name="timeOfDay"
                          value={formik.values.timeOfDay}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.timeOfDay && Boolean(formik.errors.timeOfDay)}
                          className={styles.selectBackground}
                        >
                          <MenuItem value="Select option" disabled>Select option</MenuItem>
                          {getTimeOptions15Min(formik.values.timezone).map(opt => (
                            <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    ) : (
                      <FormControl fullWidth>
                        <Select
                          id="timeOfDay"
                          name="timeOfDay"
                          value={formik.values.timeOfDay}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.timeOfDay && Boolean(formik.errors.timeOfDay)}
                          className={styles.selectBackground}
                        >
                          <MenuItem value="Select option" disabled>Select option</MenuItem>
                          {timeOfDayOptions.map((time) => (
                            <MenuItem key={time} value={time}>
                              {time}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {formik.touched.timeOfDay && formik.errors.timeOfDay && (
                      <Typography color="error" variant="caption" className={styles.errorText}>
                        {formik.errors.timeOfDay}
                      </Typography>
                    )}
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                      {tabIndex === 4 ? getFieldLabels(tabIndex).races : getFieldLabels(tabIndex).frequency}
                    </Typography>
                  {tabIndex === 2 || tabIndex === 3 ? (
                      <TextField
                        type="date"
                        fullWidth
                        name="raceDate"
                        value={formik.values.raceDate ? (() => {
                          const d = formik.values.raceDate;
                          const day = d.getDate().toString().padStart(2, '0');
                          const month = (d.getMonth() + 1).toString().padStart(2, '0');
                          const year = d.getFullYear();
                          return `${year}-${month}-${day}`;
                        })() : ''}
                        onChange={(e) => {
                          const selectedDate = new Date(e.target.value);
                          formik.setFieldValue('raceDate', selectedDate);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.raceDate && Boolean(formik.errors.raceDate)}
                        className={styles.selectBackground}
                        inputProps={{
                          min: new Date().toISOString().split('T')[0],
                          max: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                        }}
                      />
                    ) : tabIndex === 4 ? (
                      <FormControl fullWidth>
                        <Select
                          id="races"
                          name="races"
                          value={formik.values.races}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.races && Boolean(formik.errors.races)}
                          className={styles.selectBackground}
                        >
                          {Array.from({ length: 22 }, (_, i) => i + 1).map((n) => (
                            <MenuItem key={n} value={n}>
                              {n}
                            </MenuItem>
                          ))}
                        </Select>
                        {formik.touched.races && formik.errors.races && (
                          <Typography color="error" variant="caption" className={styles.errorText}>
                            {formik.errors.races}
                          </Typography>
                        )}
                      </FormControl>
                    ) : (
                      <FormControl fullWidth>
                        <Select
                          id="frequency"
                          name="frequency"
                          value={formik.values.frequency}
                          onChange={formik.handleChange}
                          onBlur={formik.handleBlur}
                          error={formik.touched.frequency && Boolean(formik.errors.frequency)}
                          className={styles.selectBackground}
                        >
                          <MenuItem value="Select option" disabled>Select option</MenuItem>
                          {tabIndex === 0 ? frequencyOptions.map((freq) => (
                            <MenuItem key={freq} value={freq}>
                              {freq}
                            </MenuItem>
                          )) : tabIndex === 1 ? unrankedRunTimeOptions.map((runTime) => (
                            <MenuItem key={runTime} value={runTime}>
                              {runTime}
                            </MenuItem>
                          )) : frequencyOptions.map((freq) => (
                            <MenuItem key={freq} value={freq}>
                              {freq}
                            </MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    )}
                    {formik.touched.frequency && formik.errors.frequency && (
                      <Typography color="error" variant="caption" className={styles.errorText}>
                        {formik.errors.frequency}
                      </Typography>
                    )}
                  </Grid>
                  {tabIndex === 4 && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                        Start Date
                      </Typography>
                      <TextField
                        type="date"
                        fullWidth
                        name="leagueStartDate"
                        value={formik.values.leagueStartDate ? (() => {
                          const d = formik.values.leagueStartDate;
                          const day = d.getDate().toString().padStart(2, '0');
                          const month = (d.getMonth() + 1).toString().padStart(2, '0');
                          const year = d.getFullYear();
                          return `${year}-${month}-${day}`;
                        })() : ''}
                        onChange={(e) => {
                          const selectedDate = new Date(e.target.value);
                          formik.setFieldValue('leagueStartDate', selectedDate);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.leagueStartDate && Boolean(formik.errors.leagueStartDate)}
                        helperText={formik.touched.leagueStartDate && formik.errors.leagueStartDate}
                        className={styles.selectBackground}
                        inputProps={{
                          min: new Date().toISOString().split('T')[0],
                        }}
                      />
                    </Grid>
                  )}
                  {tabIndex === 1 && (
                    <Grid item xs={12} sm={6}>
                      <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                        Lobby Date
                      </Typography>
                      <TextField
                        type="date"
                        fullWidth
                        name="raceDate"
                        value={formik.values.raceDate ? (() => {
                          const d = formik.values.raceDate;
                          const day = d.getDate().toString().padStart(2, '0');
                          const month = (d.getMonth() + 1).toString().padStart(2, '0');
                          const year = d.getFullYear();
                          return `${year}-${month}-${day}`;
                        })() : ''}
                        onChange={(e) => {
                          const selectedDate = new Date(e.target.value);
                          formik.setFieldValue('raceDate', selectedDate);
                        }}
                        onBlur={formik.handleBlur}
                        error={formik.touched.raceDate && Boolean(formik.errors.raceDate)}
                        helperText={formik.touched.raceDate && formik.errors.raceDate}
                        className={styles.selectBackground}
                        inputProps={{
                          min: new Date().toISOString().split('T')[0],
                          max: new Date(Date.now() + 14 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
                        }}
                      />
                    </Grid>
                  )}
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                      {getFieldLabels(tabIndex).crossplay}
                    </Typography>
                    <FormControl fullWidth>
                      <Select
                        id="crossplay"
                        name="crossplay"
                        value={formik.values.crossplay}
                        onChange={formik.handleChange}
                        onBlur={formik.handleBlur}
                        error={formik.touched.crossplay && Boolean(formik.errors.crossplay)}
                        className={styles.selectBackground}
                      >
                        <MenuItem value="Select option" disabled>Select option</MenuItem>
                        {crossplayOptions.map((option) => (
                          <MenuItem key={option} value={option}>
                            {option}
                          </MenuItem>
                        ))}
                      </Select>
                      {formik.touched.crossplay && formik.errors.crossplay && (
                        <Typography color="error" variant="caption" className={styles.errorText}>
                          {formik.errors.crossplay}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>

                  {(tabIndex === 2 || tabIndex === 3) && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="subtitle2" gutterBottom className={styles.fieldLabel}>
                            Discord Link
                          </Typography>
                          <TextField
                            fullWidth
                            name="discordLink"
                            value={formik.values.discordLink}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Enter your league Discord"
                            error={formik.touched.discordLink && Boolean(formik.errors.discordLink)}
                            className={styles.selectBackground}
                            sx={{ width: '100%' }}
                          />
                          {formik.touched.discordLink && formik.errors.discordLink && (
                            <Typography color="error" variant="caption" className={styles.errorText} sx={{ mt: 1, display: 'block' }}>
                              {formik.errors.discordLink}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ width: '100%', mt: 2 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formik.values.fullTimeSeat}
                                onChange={formik.handleChange}
                                name="fullTimeSeat"
                                onBlur={formik.handleBlur}
                              />
                            }
                            label={getFieldLabels(tabIndex).fullTimeSeat}
                            className={styles.buttonMinWidth}
                          />
                        </Box>
                        <Box sx={{ width: '100%', mt: 2 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formik.values.lobbyStreamed}
                                onChange={formik.handleChange}
                                name="lobbyStreamed"
                                color="primary"
                              />
                            }
                            label="Lobby Streamed"
                            className={styles.buttonMinWidth}
                          />
                          {formik.values.lobbyStreamed && (
                            <Box sx={{ mt: 1, ml: 3, display: 'flex', alignItems: 'center' }}>
                              <Typography variant="subtitle2" gutterBottom sx={{ mr: 2 }}>
                                Streaming Platform
                              </Typography>
                              <RadioGroup
                                row
                                name="streamingPlatform"
                                value={formik.values.streamingPlatform}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              >
                                <MuiFormControlLabel value="YouTube" control={<Radio color="primary" />} label="YouTube" />
                                <MuiFormControlLabel value="Twitch" control={<Radio color="primary" />} label="Twitch" />
                              </RadioGroup>
                              {formik.touched.streamingPlatform && formik.errors.streamingPlatform && (
                                <Typography color="error" variant="caption" className={styles.errorText} sx={{ ml: 2 }}>
                                  {formik.errors.streamingPlatform}
                                </Typography>
                              )}
                              {formik.values.lobbyStreamed && (formik.values.streamingPlatform === 'YouTube' || formik.values.streamingPlatform === 'Twitch') && (
                                <Box
                                  sx={{
                                    mt: { xs: 2, sm: 0 },
                                    ml: 0,
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { xs: 'stretch', sm: 'center' }
                                  }}
                                >
                                  <Typography variant="subtitle2" gutterBottom sx={{ mr: { xs: 0, sm: 2 }, mb: { xs: 1, sm: 0 } }}>
                                    Stream Link
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    name="streamLink"
                                    value={formik.values.streamLink}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder={formik.values.streamingPlatform === 'YouTube' ? "Enter your YouTube channel URL" : "Enter your Twitch channel URL"}
                                    error={formik.touched.streamLink && Boolean(formik.errors.streamLink)}
                                    className={styles.selectBackground}
                                    sx={{ width: '100%' }}
                                  />
                                  {formik.touched.streamLink && formik.errors.streamLink && (
                                    <Typography color="error" variant="caption" className={styles.errorText} sx={{ mt: 1 }}>
                                      {formik.errors.streamLink}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {tabIndex === 1 && (
                    <Grid item xs={12} sx={{ mt: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={formik.values.lobbyStreamed}
                            onChange={formik.handleChange}
                            name="lobbyStreamed"
                            color="primary"
                          />
                        }
                        label="Lobby Streamed"
                        className={styles.buttonMinWidth}
                      />
                      {formik.values.lobbyStreamed && (
                        <Box sx={{ mt: 2, width: '100%', display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'stretch', sm: 'center' } }}>
                          <Typography variant="subtitle2" gutterBottom sx={{ mr: { xs: 0, sm: 2 }, mb: { xs: 1, sm: 0 } }}>
                            Streaming Platform
                          </Typography>
                          <RadioGroup
                            row={false}
                            name="streamingPlatform"
                            value={formik.values.streamingPlatform}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            sx={{ flexDirection: { xs: 'row', sm: 'row' }, mb: { xs: 2, sm: 0 } }}
                          >
                            <MuiFormControlLabel value="YouTube" control={<Radio color="primary" />} label="YouTube" />
                            <MuiFormControlLabel value="Twitch" control={<Radio color="primary" />} label="Twitch" />
                          </RadioGroup>
                          {formik.touched.streamingPlatform && formik.errors.streamingPlatform && (
                            <Typography color="error" variant="caption" className={styles.errorText} sx={{ ml: { xs: 0, sm: 2 }, mb: { xs: 1, sm: 0 } }}>
                              {formik.errors.streamingPlatform}
                            </Typography>
                          )}
                          {formik.values.lobbyStreamed && (formik.values.streamingPlatform === 'YouTube' || formik.values.streamingPlatform === 'Twitch') && (
                            <TextField
                              fullWidth
                              name="streamLink"
                              value={formik.values.streamLink}
                              onChange={formik.handleChange}
                              onBlur={formik.handleBlur}
                              placeholder={formik.values.streamingPlatform === 'YouTube' ? "Enter your YouTube channel URL" : "Enter your Twitch channel URL"}
                              error={formik.touched.streamLink && Boolean(formik.errors.streamLink)}
                              className={styles.selectBackground}
                              sx={{ width: '100%', mt: { xs: 2, sm: 0 } }}
                            />
                          )}
                          {formik.touched.streamLink && formik.errors.streamLink && (
                            <Typography color="error" variant="caption" className={styles.errorText} sx={{ mt: 1 }}>
                              {formik.errors.streamLink}
                            </Typography>
                          )}
                        </Box>
                      )}
                    </Grid>
                  )}

                  {tabIndex === 4 && (
                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                        <Box sx={{ width: '100%' }}>
                          <Typography variant="subtitle2" gutterBottom className={styles.fieldLabel}>
                            League Discord Link
                          </Typography>
                          <TextField
                            fullWidth
                            name="discordLink"
                            value={formik.values.discordLink}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            placeholder="Enter your league Discord"
                            error={formik.touched.discordLink && Boolean(formik.errors.discordLink)}
                            className={styles.selectBackground}
                            sx={{ width: '100%' }}
                          />
                          {formik.touched.discordLink && formik.errors.discordLink && (
                            <Typography color="error" variant="caption" className={styles.errorText} sx={{ mt: 1, display: 'block' }}>
                              {formik.errors.discordLink}
                            </Typography>
                          )}
                        </Box>
                        <Box sx={{ width: '100%', mt: 2 }}>
                          <FormControlLabel
                            control={
                              <Checkbox
                                checked={formik.values.lobbyStreamed}
                                onChange={formik.handleChange}
                                name="lobbyStreamed"
                                color="primary"
                              />
                            }
                            label="Races Streamed"
                            className={styles.buttonMinWidth}
                          />
                          {formik.values.lobbyStreamed && (
                            <Box sx={{ mt: 1, ml: 3, display: 'flex', alignItems: 'center' }}>
                              <Typography variant="subtitle2" gutterBottom sx={{ mr: 2 }}>
                                Streaming Platform
                              </Typography>
                              <RadioGroup
                                row
                                name="streamingPlatform"
                                value={formik.values.streamingPlatform}
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                              >
                                <MuiFormControlLabel value="YouTube" control={<Radio color="primary" />} label="YouTube" />
                                <MuiFormControlLabel value="Twitch" control={<Radio color="primary" />} label="Twitch" />
                              </RadioGroup>
                              {formik.touched.streamingPlatform && formik.errors.streamingPlatform && (
                                <Typography color="error" variant="caption" className={styles.errorText} sx={{ ml: 2 }}>
                                  {formik.errors.streamingPlatform}
                                </Typography>
                              )}
                              {formik.values.lobbyStreamed && (formik.values.streamingPlatform === 'YouTube' || formik.values.streamingPlatform === 'Twitch') && (
                                <Box
                                  sx={{
                                    mt: { xs: 2, sm: 0 },
                                    ml: 0,
                                    width: '100%',
                                    display: 'flex',
                                    flexDirection: { xs: 'column', sm: 'row' },
                                    alignItems: { xs: 'stretch', sm: 'center' }
                                  }}
                                >
                                  <Typography variant="subtitle2" gutterBottom sx={{ mr: { xs: 0, sm: 2 }, mb: { xs: 1, sm: 0 } }}>
                                    Stream Link
                                  </Typography>
                                  <TextField
                                    fullWidth
                                    name="streamLink"
                                    value={formik.values.streamLink}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    placeholder={formik.values.streamingPlatform === 'YouTube' ? "Enter your YouTube channel URL" : "Enter your Twitch channel URL"}
                                    error={formik.touched.streamLink && Boolean(formik.errors.streamLink)}
                                    className={styles.selectBackground}
                                    sx={{ width: '100%' }}
                                  />
                                  {formik.touched.streamLink && formik.errors.streamLink && (
                                    <Typography color="error" variant="caption" className={styles.errorText} sx={{ mt: 1 }}>
                                      {formik.errors.streamLink}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                            </Box>
                          )}
                        </Box>
                      </Box>
                    </Grid>
                  )}

                  {(tabIndex === 0 || tabIndex === 4) && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle1" gutterBottom className={styles.fieldLabel}>
                        {getFieldLabels(tabIndex).preferredDays}
                      </Typography>
                      <FormGroup row>
                        {(tabIndex === 4 ? daysOfWeek.filter(d => d !== 'Any days') : daysOfWeek).map((day) => (
                          <FormControlLabel
                            key={day}
                            control={
                              <Checkbox
                                checked={formik.values.preferredDays[day]}
                                onChange={(e) => {
                                  const isChecked = e.target.checked;
                                  
                                  if (day === 'Any days' && isChecked) {
                                    // If "Any days" is checked, uncheck all other days
                                    const newPreferredDays = {};
                                    daysOfWeek.forEach(d => {
                                      newPreferredDays[d] = d === 'Any days';
                                    });
                                    formik.setFieldValue('preferredDays', newPreferredDays);
                                  } else if (day !== 'Any days' && isChecked) {
                                    // If a specific day is checked, uncheck "Any days"
                                    formik.setFieldValue(`preferredDays.${day}`, true);
                                    formik.setFieldValue('preferredDays.Any days', false);
                                  } else {
                                    // Normal checkbox behavior
                                    formik.setFieldValue(`preferredDays.${day}`, isChecked);
                                  }
                                }}
                                name={`preferredDays.${day}`}
                                onBlur={formik.handleBlur}
                              />
                            }
                            label={day}
                            className={styles.buttonMinWidth}
                          />
                        ))}
                      </FormGroup>
                      {formik.touched.preferredDays && formik.errors.preferredDays && (
                        <Typography color="error" variant="caption" className={styles.errorText}>
                          {formik.errors.preferredDays}
                        </Typography>
                      )}
                    </Grid>
                  )}
                </Grid>
              </Box>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Box className={styles.flexEnd} sx={{ mt: 2 }}>
              <Button
                type={isAuthenticated() && isEmailVerified() ? "submit" : "button"}
                variant="contained"
                color="primary"
                size="large"
                disabled={isAuthenticated() && !isEmailVerified()}
                onClick={() => !isAuthenticated() && navigate('/register')}
                className={styles.buttonLarge}
              >
                {!isAuthenticated() ? 'Sign in to Create Race Card' : 
                 !isEmailVerified() ? 'Verify Email to Create Race Card' : 
                 'Create Race Card'}
              </Button>
            </Box>
          </Grid>
        </Grid>
      </form>
    );
  };

  // TabPanel component
  const TabPanel = ({ children, value, index }) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`tabpanel-${index}`}
        aria-labelledby={`tab-${index}`}
      >
        {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
      </div>
    );
  };

  return (
    <Container maxWidth="md" className={styles.pageContainer}>
      {/* Email Verification Reminder */}
      {isAuthenticated() && !isEmailVerified() && (
        <Alert 
          severity="warning" 
          className={styles.alertMargin}
          action={
            <Button 
              color="inherit" 
              size="small"
              onClick={() => {
                // Add resend verification email functionality
                console.log('Resend verification email');
              }}
            >
              Resend Email
            </Button>
          }
        >
          Please verify your email address to create race cards and apply to others. Check your inbox for a verification link.
        </Alert>
      )}

      <Paper
        elevation={6}
        className={styles.formContainer}
        sx={{ background: '#fff', boxShadow: 3, p: { xs: '12px', md: 4 }, borderRadius: 2 }}
      >
        <Typography variant="h4" component="h1" gutterBottom sx={{ mb: 4, color: 'primary.main', fontWeight: 'bold', textAlign: 'center' }}>
          Create Race Card
        </Typography>
        {error && (
          <Alert severity="error" className={styles.alertMargin}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" className={styles.alertMargin}>
            {success}
          </Alert>
        )}
        {isMobile ? (
          <Box sx={{ mb: 0 }}>
            <Grid container spacing={1}>
              {[
                { label: '2 Player Career', index: 0 },
                { label: 'Unranked Lobby', index: 1 },
                { label: 'Reserve a Race', index: 2 },
                { label: 'Commentary', index: 3 },
                { label: 'League Startup', index: 4 },
              ].map((t) => (
                <Grid item xs={6} key={t.index}>
                  <Button
                    fullWidth
                    onClick={() => setActiveTab(t.index)}
                    variant={activeTab === t.index ? 'contained' : 'outlined'}
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: 2,
                      height: 44,
                    }}
                  >
                    {t.label}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Box>
        ) : (
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="fullWidth"
            textColor="primary"
            indicatorColor="primary"
            sx={{
              borderRadius: 2,
              mb: 0,
              background: '#f5f7fa',
              boxShadow: 1,
              '.MuiTab-root': {
                fontWeight: 600,
                fontSize: '1.1rem',
                color: '#1976d2',
                textTransform: 'none',
                '&.Mui-selected': {
                  color: '#fff',
                  background: '#1976d2',
                  borderRadius: 2,
                },
                transition: 'background 0.2s, color 0.2s',
              },
              '.MuiTabs-indicator': {
                height: 0,
              },
            }}
          >
            <Tab label="2 Player Career" />
            <Tab label="Unranked Lobby" />
            <Tab label="Reserve a Race" />
            <Tab label="Commentary" />
            <Tab label="League Startup" />
          </Tabs>
        )}
        <TabPanel value={activeTab} index={0}>
          {availableTypes.career ? (
            <RaceCardForm tabIndex={0} />
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                You already have a 2 Player Career card.
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please close your active card below to create a new one.
              </Typography>
            </Box>
          )}
        </TabPanel>
        <TabPanel value={activeTab} index={1}>
          {availableTypes.unranked ? (
            <RaceCardForm tabIndex={1} />
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                You already have an Unranked Lobby card. 
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please close your active card below to create a new one.
              </Typography>
            </Box>
          )}
        </TabPanel>
        <TabPanel value={activeTab} index={2}>
          {availableTypes.reserve ? (
            <RaceCardForm tabIndex={2} />
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                You already have a Reserve a Race card. 
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please close your active card below to create a new one.
              </Typography>
            </Box>
          )}
        </TabPanel>
        <TabPanel value={activeTab} index={3}>
          {availableTypes.commentary ? (
            <RaceCardForm tabIndex={3} />
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                You already have a Commentary card. 
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please close your active card below to create a new one.
              </Typography>
            </Box>
          )}
        </TabPanel>
        <TabPanel value={activeTab} index={4}>
          {availableTypes.leagueStartup ? (
            <RaceCardForm tabIndex={4} />
          ) : (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="body1" color="text.secondary">
                You already have a League Startup card.
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Please close your active card below to create a new one.
              </Typography>
            </Box>
          )}
        </TabPanel>
      </Paper>

      {/* User's Active Race Card Section */}
      <Paper elevation={6} className={styles.formContainer} data-section="active-cards">
        <Typography 
          variant="h4" 
          component="h1" 
          gutterBottom 
          sx={{ 
            textAlign: 'center',
            mb: 4,
            color: 'primary.main',
            fontWeight: 'bold'
          }}
        >
          Your Active Race Cards{' '}
          <Typography component="span" variant="body2" color="text.secondary" sx={{ fontSize: '0.6em' }}>
            ({userRaceCards.length}/5)
          </Typography>
        </Typography>

        {loading ? (
          <Box className={styles.loadingContainer}>
            <CircularProgress />
          </Box>
        ) : userRaceCards && userRaceCards.length > 0 ? (
          <Box>
            {userRaceCards.map((userRaceCard) => (
              <Box key={userRaceCard.id} sx={{ mb: 3 }}>
                <Card elevation={6}>
                  <CardContent>
                    <Typography variant="h5" component="h2" gutterBottom className={styles.titlePrimary}>
                      {userRaceCard.title}
                    </Typography>
                    
                    {/* Race Preferences Section */}
                    <Box className={styles.sectionBox}>
                      <Typography variant="subtitle1" className={styles.subtitlePrimary}>
                        {userRaceCard.cardType === 'career' ? 'Race Preferences:' : 
                         userRaceCard.cardType === 'unranked' ? 'Lobby Preferences:' :
                         (userRaceCard.cardType === 'reserve' || userRaceCard.cardType === 'commentary') ? 'Race Details:' : 
                         userRaceCard.cardType === 'leagueStartup' ? 'League Preferences:' : 'Race Preferences:'}
                      </Typography>
                      <Box className={styles.chipsContainer}>
                        {/* Game version (when set) */}
                        {userRaceCard.gameVersion && (
                          <Chip
                            label={GAME_VERSIONS.find(g => g.value === userRaceCard.gameVersion)?.label || userRaceCard.gameVersion}
                            size="medium"
                            sx={{ bgcolor: '#c00', color: 'white' }}
                          />
                        )}
                        {/* Career Card Fields */}
                        {(userRaceCard.cardType === 'career' || userRaceCard.cardType === 'leagueStartup') && (
                          <>
                            {userRaceCard.cardType === 'career' && (
                              <>
                                <Chip
                                  label={userRaceCard.teamPreference}
                                  color="primary"
                                  size="medium"
                                  sx={{ 
                                    bgcolor: teamColors[userRaceCard.teamPreference]?.bg || '#000000',
                                    color: teamColors[userRaceCard.teamPreference]?.color || '#ffffff',
                                    border: '1px solid #000000'
                                  }}
                                />
                                <Chip
                                  label={userRaceCard.rivalryType}
                                  color="primary"
                                  size="medium"
                                  sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                                />
                              </>
                            )}
                            <Chip
                              label={(userRaceCard.cardType === 'leagueStartup' && userRaceCard.aiLevel === 'Multiple Tiers') ? 'Multiple Tiers' : `${userRaceCard.aiLevel} AI`}
                              color="primary"
                              size="medium"
                              variant={userRaceCard.cardType === 'leagueStartup' ? 'filled' : 'outlined'}
                              sx={userRaceCard.cardType === 'leagueStartup' ? { bgcolor: 'black', color: 'white' } : { borderColor: 'black', color: 'black' }}
                            />
                            <Chip
                              label={userRaceCard.raceDistance}
                              color="primary"
                              size="medium"
                              variant="outlined"
                              sx={{ borderColor: 'black', color: 'black' }}
                            />
                            {userRaceCard.cardType === 'leagueStartup' && userRaceCard.qualifyingFormat && (
                              <Chip
                                label={userRaceCard.qualifyingFormat}
                                color="primary"
                                size="medium"
                                sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                              />
                            )}
                            {userRaceCard.cardType === 'leagueStartup' && userRaceCard.damage && (
                              <Chip
                                label={userRaceCard.damage}
                                color="primary"
                                size="medium"
                                sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                              />
                            )}
                            {userRaceCard.cardType === 'leagueStartup' && userRaceCard.assists && (
                              <Chip
                                label={userRaceCard.assists}
                                color="primary"
                                size="medium"
                                sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                              />
                            )}
                          </>
                        )}

                        {/* Unranked Card Fields */}
                        {userRaceCard.cardType === 'unranked' && (
                          <>
                            <Chip
                              label={userRaceCard.carType}
                              color="primary"
                              size="medium"
                              sx={{ bgcolor: 'black', color: 'white' }}
                            />
                            <Chip
                              label={userRaceCard.qualifyingPreference}
                              color="primary"
                              size="medium"
                              variant="outlined"
                              sx={{ borderColor: 'black', color: 'black' }}
                            />
                            <Chip
                              label={userRaceCard.damage}
                              color="primary"
                              size="medium"
                              sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                            />
                            <Chip
                              label={userRaceCard.raceDistance}
                              color="primary"
                              size="medium"
                              variant="outlined"
                              sx={{ borderColor: 'black', color: 'black' }}
                            />
                          </>
                        )}

                        {/* Reserve Card Fields */}
                        {(userRaceCard.cardType === 'reserve' || userRaceCard.cardType === 'commentary') && (
                          <>
                            <Chip
                              label={userRaceCard.track}
                              color="primary"
                              size="medium"
                              sx={{ bgcolor: 'black', color: 'white' }}
                            />
                            {userRaceCard.cardType === 'reserve' && (
                              <Chip
                                label={`${userRaceCard.estAiLevel} AI`}
                                color="primary"
                                size="medium"
                                variant="outlined"
                                sx={{ borderColor: 'black', color: 'black' }}
                              />
                            )}
                            <Chip
                              label={userRaceCard.raceDistance}
                              color="primary"
                              size="medium"
                              sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                            />
                            <Chip
                              label={userRaceCard.carClass}
                              color="primary"
                              size="medium"
                              variant="outlined"
                              sx={{ borderColor: 'black', color: 'black' }}
                            />
                             <Chip
                               label={userRaceCard.qualifyingFormat}
                               color="primary"
                               size="medium"
                               sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                             />
                             {userRaceCard.performance && userRaceCard.cardType === 'reserve' && (
                              <Chip
                                label={userRaceCard.performance}
                                color="primary"
                                size="medium"
                                variant="outlined"
                                sx={{ borderColor: 'black', color: 'black' }}
                              />
                            )}
                            {userRaceCard.seats && (userRaceCard.cardType === 'reserve') && (
                              <Chip
                                label={`${Math.max(0, userRaceCard.seats - (applications[userRaceCard.id]?.filter(app => app.status === 'accepted').length || 0))} ${Math.max(0, userRaceCard.seats - (applications[userRaceCard.id]?.filter(app => app.status === 'accepted').length || 0)) === 1 ? 'seat' : 'seats'}`}
                                color="primary"
                                size="medium"
                                sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                              />
                            )}
                          </>
                        )}

                        {/* Unranked Streaming Platform Chips */}
                        {userRaceCard.cardType === 'unranked' && userRaceCard.streamingPlatform === 'YouTube' && (
                          <a href={userRaceCard.streamLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <img src="/images/youtube-logo.png" alt="YouTube" style={{ height: 20, width: 90, margin: '4px 4px', verticalAlign: 'middle', cursor: 'pointer' }} />
                          </a>
                        )}
                        {userRaceCard.cardType === 'unranked' && userRaceCard.streamingPlatform === 'Twitch' && (
                          <a href={userRaceCard.streamLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <img src="/images/twitch-logo.png" alt="Twitch" style={{ height: 22, width: 60, margin: '4px 4px', verticalAlign: 'middle', cursor: 'pointer' }} />
                          </a>
                        )}
                        {/* Reserve / Commentary / League Startup Streaming Platform Chips */}
                        {(userRaceCard.cardType === 'reserve' || userRaceCard.cardType === 'commentary' || userRaceCard.cardType === 'leagueStartup') && userRaceCard.streamingPlatform === 'YouTube' && (
                          <a href={userRaceCard.streamLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <img src="/images/youtube-logo.png" alt="YouTube" style={{ height: 20, width: 90, margin: '4px 4px', verticalAlign: 'middle', cursor: 'pointer' }} />
                          </a>
                        )}
                        {(userRaceCard.cardType === 'reserve' || userRaceCard.cardType === 'commentary' || userRaceCard.cardType === 'leagueStartup') && userRaceCard.streamingPlatform === 'Twitch' && (
                          <a href={userRaceCard.streamLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <img src="/images/twitch-logo.png" alt="Twitch" style={{ height: 22, width: 60, margin: '4px 4px', verticalAlign: 'middle', cursor: 'pointer' }} />
                          </a>
                        )}
                        {(userRaceCard.cardType === 'leagueStartup' || userRaceCard.cardType === 'reserve' || userRaceCard.cardType === 'commentary') && userRaceCard.discordLink && (
                          <a href={userRaceCard.discordLink} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none' }}>
                            <img src="/images/discord-logo.png" alt="Discord" style={{ height: 18, width: 100, margin: '4px 4px', verticalAlign: 'middle', cursor: 'pointer' }} />
                          </a>
                        )}
                      </Box>
                    </Box>

                    {/* Schedule Preferences Section */}
                    <Box className={styles.sectionBox}>
                      <Typography variant="subtitle1" className={styles.subtitlePrimary}>
                        {userRaceCard.cardType === 'career' ? 'Schedule Preferences:' : 
                         userRaceCard.cardType === 'unranked' ? 'Lobby Schedule:' :
                         (userRaceCard.cardType === 'reserve' || userRaceCard.cardType === 'commentary') ? 'Race Schedule:' : 
                         userRaceCard.cardType === 'leagueStartup' ? 'Schedule Preferences:' : 'Schedule Preferences:'}
                      </Typography>
                      <Box className={styles.chipsContainer}>
                        <Chip
                          label={userRaceCard.timezone}
                          color="primary"
                          size="medium"
                          sx={{ bgcolor: 'black', color: 'white' }}
                        />
                        <Chip
                          label={userRaceCard.cardType === 'career' ? userRaceCard.timeOfDay :
                                 userRaceCard.cardType === 'leagueStartup' ? formatTimeWithAMPM(userRaceCard.timeOfDay, userRaceCard.timezone) :
                                 userRaceCard.cardType === 'unranked' ? formatTimeWithAMPM(userRaceCard.estimatedStartTime, userRaceCard.timezone) :
                                 (userRaceCard.cardType === 'reserve' || userRaceCard.cardType === 'commentary') ? formatTimeWithAMPM(userRaceCard.startTime, userRaceCard.timezone) :
                                 userRaceCard.timeOfDay}
                          color="primary"
                          size="medium"
                          variant="outlined"
                          sx={{ borderColor: 'black', color: 'black' }}
                        />
                        <Chip
                          label={
                            userRaceCard.cardType === 'career' || userRaceCard.cardType === 'leagueStartup' ? (userRaceCard.cardType === 'leagueStartup' ? `Races: ${userRaceCard.races ?? ''}` : userRaceCard.frequency) :
                            userRaceCard.cardType === 'unranked' ? userRaceCard.runTime :
                            (userRaceCard.cardType === 'reserve' || userRaceCard.cardType === 'commentary') ? (
                              userRaceCard.raceDate ? (() => {
                                const d = new Date(userRaceCard.raceDate.seconds * 1000);
                                const day = d.getDate().toString().padStart(2, '0');
                                const month = (d.getMonth() + 1).toString().padStart(2, '0');
                                const year = d.getFullYear();
                                return `${day}/${month}/${year}`;
                              })() : 'Unknown'
                            ) :
                            userRaceCard.frequency
                          }
                          color="primary"
                          size="medium"
                          sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                        />
                        {userRaceCard.cardType === 'leagueStartup' && userRaceCard.leagueStartDate && (
                          <Chip
                            label={`Start: ${formatDateDDMMYYYY(userRaceCard.leagueStartDate?.seconds ? userRaceCard.leagueStartDate.seconds * 1000 : userRaceCard.leagueStartDate)}`}
                            color="primary"
                            size="medium"
                            sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                          />
                        )}
                        {(userRaceCard.cardType === 'career' || userRaceCard.cardType === 'leagueStartup') && userRaceCard.preferredDays && 
                         Object.entries(userRaceCard.preferredDays)
                          .filter(([_, selected]) => selected)
                          .map(([day], index) => (
                            <Chip
                              key={day}
                              label={day}
                              color="primary"
                              size="medium"
                              variant={index % 2 === 0 ? "outlined" : "filled"}
                              sx={index % 2 === 0 
                                ? { borderColor: 'black', color: 'black' }
                                : { bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }
                              }
                            />
                          ))}
                        {(userRaceCard.cardType === 'reserve' || userRaceCard.cardType === 'commentary') && userRaceCard.fullTimeSeat && (
                          <Chip
                            label={userRaceCard.cardType === 'commentary' ? 'Paid Role' : 'Full Time Seat'}
                            color="primary"
                            size="medium"
                            sx={{ bgcolor: 'green', color: 'white' }}
                          />
                        )}
                        {userRaceCard.cardType === 'unranked' && userRaceCard.raceDate && (
                          <Chip
                            label={formatDateDDMMYYYY(userRaceCard.raceDate.seconds ? userRaceCard.raceDate.seconds * 1000 : userRaceCard.raceDate)}
                            color="primary"
                            size="medium"
                            sx={{ bgcolor: 'white', color: 'black', borderColor: 'black', borderWidth: 1, borderStyle: 'solid' }}
                          />
                        )}
                      </Box>
                    </Box>

                    {/* Applications Section */}
                    <Box className={styles.sectionBox}>
                      <Typography variant="subtitle1" className={styles.subtitlePrimary}>
                        Applications:
                      </Typography>
                        {console.log('All applications for card', userRaceCard.id, ':', applications[userRaceCard.id])}
                        {console.log('Pending applications for card', userRaceCard.id, ':', applications[userRaceCard.id]?.filter(app => app.status === 'pending'))}
                        {console.log('Accepted applications for card', userRaceCard.id, ':', applications[userRaceCard.id]?.filter(app => app.status === 'accepted'))}
                        
                        {/* Show accepted applications */}
                        {applications[userRaceCard.id]?.filter(app => app.status === 'accepted').length > 0 && (
                          <Box className={styles.sectionBox}>
                            {applications[userRaceCard.id]
                              ?.filter(app => app.status === 'accepted')
                              .map((application) => (
                                <Box
                                  key={application.id}
                                  className={styles.applicationAccepted}
                                >
                                  <Typography variant="body1" color="success.dark" sx={{ fontWeight: 'medium' }}>
                                    You have accepted{' '}
                                    <Typography 
                                      component="span" 
                                      variant="body1" 
                                      color="success.dark" 
                                      sx={{ fontWeight: 'bold' }}
                                    >
                                      {application.applicantEaId || 'Unknown EA ID'}
                                    </Typography>
                                    , please add them on EA.
                                  </Typography>
                                </Box>
                              ))}
                          </Box>
                        )}

                        {/* Show pending applications */}
                        {(!applications[userRaceCard.id] || applications[userRaceCard.id].filter(app => app.status === 'pending').length === 0) && 
                         (!applications[userRaceCard.id] || applications[userRaceCard.id].filter(app => app.status === 'accepted').length === 0) ? (
                          <Box className={styles.applicationEmpty}>
                            <Typography variant="body1" color="text.secondary">
                              Awaiting applications
                            </Typography>
                          </Box>
                        ) : applications[userRaceCard.id]?.filter(app => app.status === 'pending').length > 0 ? (
                          <List dense>
                            {applications[userRaceCard.id]
                              ?.filter(app => app.status === 'pending')
                              .map((application) => {
                                console.log('Processing application:', application);
                                return (
                                  <ListItem
                                    key={application.id}
                                    className={styles.applicationPending}
                                  >
                                    <ListItemText
                                      primary={`${application.applicantEaId || 'Unknown EA ID'} has applied for your ${userRaceCard.title || 'race card'}`}
                                      secondary={`Applied on ${application.createdAt?.toDate?.() ? 
                                        application.createdAt.toDate().toLocaleDateString() : 
                                        'Recently'}`}
                                    />
                                    <ListItemSecondaryAction>
                                      <IconButton
                                        edge="end"
                                        color="success"
                                        onClick={() => handleApplicationResponse(application.id, 'accepted')}
                                        disabled={processingApplication === application.id}
                                        sx={{ mr: 1 }}
                                      >
                                        {processingApplication === application.id ? (
                                          <CircularProgress size={20} />
                                        ) : (
                                          <CheckCircle />
                                        )}
                                      </IconButton>
                                      <IconButton
                                        edge="end"
                                        color="error"
                                        onClick={() => handleApplicationResponse(application.id, 'rejected')}
                                        disabled={processingApplication === application.id}
                                      >
                                        {processingApplication === application.id ? (
                                          <CircularProgress size={20} />
                                        ) : (
                                          <Cancel />
                                        )}
                                      </IconButton>
                                    </ListItemSecondaryAction>
                                  </ListItem>
                                );
                              })}
                          </List>
                        ) : null}
                    </Box>
                  </CardContent>
                  <CardActions className={styles.cardActionsEnd}>
                    <Button
                      variant="contained"
                      color="error"
                      onClick={() => handleDeleteCard(userRaceCard.id)}
                      sx={{ 
                        bgcolor: 'error.main',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'error.dark'
                        }
                      }}
                    >
                      Close Card
                    </Button>
                  </CardActions>
                </Card>
              </Box>
            ))}
          </Box>
        ) : (
          <Box className={styles.emptyState}>
            <Typography variant="h6" color="text.secondary">
              No active race cards
            </Typography>
            {!isAuthenticated() && (
              <Typography variant="body1" color="text.secondary" className={styles.emptyStateText}>
                Sign up to create your first race card!
              </Typography>
            )}
          </Box>
        )}
        
        {/* CTA Button */}
        <Box sx={{ textAlign: 'center', mt: 3 }}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            onClick={() => navigate('/race-cards')}
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              textTransform: 'none',
              px: 4,
              py: 1.5,
              fontSize: '1.1rem',
              fontWeight: 'bold',
              '&:hover': {
                bgcolor: 'primary.dark'
              }
            }}
          >
            Go to Race Cards
          </Button>
        </Box>
      </Paper>

      {/* Success/Error Notifications */}
      <Snackbar
        open={!!success}
        autoHideDuration={6000}
        onClose={() => setSuccess('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSuccess('')} severity="success" className={styles.alertFullWidth}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar
        open={!!error}
        autoHideDuration={6000}
        onClose={() => setError('')}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setError('')} severity="error" className={styles.alertFullWidth}>
          {error}
        </Alert>
      </Snackbar>
    </Container>
  );
}

export default CreateRaceCard; 